import {HttpErrorResponse} from '@angular/common/http';
import {Component, Inject, OnInit, ViewChild, ChangeDetectionStrategy} from '@angular/core';
import {FormControl, FormGroup, ValidatorFn, Validators} from '@angular/forms';
import {ActivatedRoute} from '@angular/router';
import {EMPTY, Observable, throwError} from 'rxjs';
import {catchError, takeUntil, tap} from 'rxjs/operators';
import {apiErrorArrayIncludes} from '../../common/api-error-array';
import {handleResponseError, HTTP_STATUS} from '../../common/api-helpers';
import {TypedForm, TypedFormOwnControls} from '../../common/form-types';
import {dependOn} from '../../common/rxjs/depend-on';
import {AuthService} from '../../components/auth/auth.service';
import {destroy$} from '../../components/destroyable';
import {PARTIAL_LOADING, PartialLoading} from '../../components/partial-loading-indicator';
import {TWO_FACTOR_AUTH_ERRORS} from '../../enums/two-factor-auth-error';
import {ROUTE_REDIRECTS_TOKEN, RouteRedirects} from '../../models/route-redirects';
import {ChangePasswordError, SignUpService} from '../../services/sign-up.service';
import {UserService} from '../../services/user.service';

enum ChangePasswordPageState {
    FORM,
    SUCCESS,
    ERROR
}

interface ChangePasswordFormValue {
    currentPassword: string;
    password: string;
    repeatPassword: string;
    twoFACode: string;
}

type ChangePasswordForm = TypedForm<ChangePasswordFormValue>;

interface ChangePasswordFormErrors {
    commonError?: string;
}

export interface BackButton {
    routerLink: any[] | string;
    buttonText: string;
}

const FORM_ERROR_MESSAGES = {
    emptyFields:          'components.change-password-page.error_empty_fields',
    passwordMismatch:     'components.change-password-page.error_password_mismatch',
    wrongCode:            'components.change-password-page.error_wrong_code',
    emptyCode:            'components.change-password-page.error_code_is_empty',
    wrongCurrentPassword: 'components.change-password-page.error_current_password_incorrect'
};

const FORM_ERROR_DIFFERENT_PASSWORDS = 'notSame';

@Component({
    selector: 'change-password-page',
    templateUrl: './change-password-page.component.html',
    styleUrls: ['./change-password-page.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class ChangePasswordPageComponent implements OnInit {

    private static checkPassword(group: ChangePasswordForm): null | {[FORM_ERROR_DIFFERENT_PASSWORDS]: boolean} {
        const pass = group.controls.password.value;
        const confirmPass = group.controls.repeatPassword.value;

        return pass === confirmPass ? null : {[FORM_ERROR_DIFFERENT_PASSWORDS]: true};
    }

    private static isCurrentPasswordErrorResponse(errorResponse: HttpErrorResponse): boolean {
        return errorResponse.status === HTTP_STATUS.BAD_REQUEST
            && apiErrorArrayIncludes(errorResponse.error, ChangePasswordError.PASSWORD_IS_INVALID);
    }

    private static isCodeErrorResponse(errorResponse: HttpErrorResponse): boolean {
        return errorResponse.status === HTTP_STATUS.BAD_REQUEST
            && apiErrorArrayIncludes(errorResponse.error, ...TWO_FACTOR_AUTH_ERRORS);
    }

    public backButton: BackButton;

    @ViewChild('loadingIndicator', {read: PARTIAL_LOADING, static: true})
    public loadingIndicator: PartialLoading;

    public readonly ChangePasswordPageState = ChangePasswordPageState;

    public state: ChangePasswordPageState = ChangePasswordPageState.FORM;

    public form: ChangePasswordForm;

    public formErrors: ChangePasswordFormErrors;

    public isAuthorized: boolean;

    public isTwoFACodeRequired: boolean;

    public doFocusTwoFACode: boolean = false;

    public readonly destroy$ = destroy$();

    constructor(
        @Inject(ROUTE_REDIRECTS_TOKEN)
        protected routeRedirects: RouteRedirects,
        protected signUpService: SignUpService,
        protected authService: AuthService,
        protected userService: UserService,
        protected activatedRoute: ActivatedRoute
    ) {}

    public ngOnInit(): void {
        this.isAuthorized = !!this.authService.userRole;
        this.isTwoFACodeRequired = this.isAuthorized && this.userService.user.enabled2FA;

        this.buildForm();
        this.initBackButton();
    }

    public submit(event: Event): void {
        event.preventDefault();

        this.formErrors = null;

        if (!this.loadingIndicator.getLoadingState() && this.form.valid) {
            if (this.isAuthorized) {
                this.sendAuthorizedChangePasswordRequest();
            } else {
                this.sendUnauthorizedChangePasswordRequest();
            }
        } else if (this.form.hasError(FORM_ERROR_DIFFERENT_PASSWORDS)) {
            this.setFormErrors({
                commonError: FORM_ERROR_MESSAGES.passwordMismatch
            });
        } else {
            this.setFormErrors({
                commonError: FORM_ERROR_MESSAGES.emptyFields
            });
        }
    }

    private sendUnauthorizedChangePasswordRequest(): void {
        const resetPasswordRequestCode = this.activatedRoute.snapshot.params['key'];
        const {password, twoFACode}    = (this.form.value as ChangePasswordFormValue);

        const targetServiceRequest = this.signUpService.requestPasswordChange(
            resetPasswordRequestCode,
            password,
            twoFACode
        );

        this.listenToPasswordChangeResponse(targetServiceRequest);
    }

    private sendAuthorizedChangePasswordRequest(): void {
        const {currentPassword, password, twoFACode} = (this.form.value as ChangePasswordFormValue);
        const targetServiceRequest = this.userService.requestPasswordChange(
            currentPassword,
            password,
            twoFACode
        );

        this.listenToPasswordChangeResponse(targetServiceRequest);
    }

    private listenToPasswordChangeResponse(request: Observable<any>): void {
        request.pipe(
            dependOn(this.loadingIndicator.loading$),
            tap(() => this.setSuccessState()),
            catchError((error: HttpErrorResponse) =>
                error instanceof HttpErrorResponse
                    ? this.handleErrorResponse(error)
                    : throwError(error)
            ),
            takeUntil(this.destroy$)
        ).subscribe();
    }

    private setSuccessState(): void {
        this.state = ChangePasswordPageState.SUCCESS;
    }

    private showResponseError(errorResponse: HttpErrorResponse): void {
        console.warn('Error while password changing', errorResponse);

        this.setErrorState();
    }

    private buildForm(): void {
        const controls: TypedFormOwnControls<ChangePasswordFormValue> = {
            currentPassword: new FormControl(
                {
                    value:    '',
                    disabled: !this.isAuthorized
                },
                Validators.required
            ),
            password:        new FormControl('', Validators.required),
            repeatPassword:  new FormControl(''),
            twoFACode:       new FormControl(
                {
                    value:    '',
                    disabled: !this.isTwoFACodeRequired
                },
                Validators.required
            )
        };

        this.form = new FormGroup(
            controls,
            {
                validators: ChangePasswordPageComponent.checkPassword as unknown as ValidatorFn
            }
        ) as ChangePasswordForm;
    }

    private setErrorState(): void {
        this.state = ChangePasswordPageState.ERROR;
    }

    private setFormErrors(errors: ChangePasswordFormErrors): void {
        this.formErrors = Object.assign(this.formErrors || {}, errors);
    }

    private initBackButton(): void {
        this.backButton = this.isAuthorized
            ? {
                routerLink: [this.routeRedirects.DASHBOARD_ROUTES[this.authService.userRole]],
                buttonText: 'components.change-password-page.back_to_dashboard'
            }
            : {
                routerLink: [this.routeRedirects.LOGIN_ROUTE],
                buttonText: 'components.change-password-page.back_to_login'
            };
    }

    private handleErrorResponse(error: HttpErrorResponse): Observable<never> {
        return ChangePasswordPageComponent.isCodeErrorResponse(error)
            ? this.handleWrongCodeErrorResponse()
            : ChangePasswordPageComponent.isCurrentPasswordErrorResponse(error)
                ? this.handleFormErrorResponse({commonError: FORM_ERROR_MESSAGES.wrongCurrentPassword})
                : this.handleUnknownErrorResponse(error);
    }

    private handleWrongCodeErrorResponse(): Observable<never> {
        if (this.isTwoFACodeRequired) {
            this.setFormErrors({commonError: FORM_ERROR_MESSAGES.wrongCode});
        } else {
            this.isTwoFACodeRequired = true;
            this.doFocusTwoFACode = true;

            this.form.controls.twoFACode.enable();
            this.setFormErrors({commonError: FORM_ERROR_MESSAGES.emptyCode});
        }

        return EMPTY;
    }

    private handleFormErrorResponse(errors: ChangePasswordFormErrors): Observable<never> {
        this.setFormErrors(errors);

        return EMPTY;
    }

    private handleUnknownErrorResponse(error: HttpErrorResponse): Observable<never> {
        return handleResponseError(error).pipe(
            catchError(({message}) => {
                this.showResponseError(message);

                return EMPTY;
            })
        );
    }

}
